function getCallTimestamp(call = {}) {
  const value = call.created_at || call.updated_at || call.started_at || "";
  const timestamp = new Date(value).getTime();

  if (Number.isNaN(timestamp)) {
    return 0;
  }

  return timestamp;
}

function getFirstPresentValue(calls = [], key) {
  for (const call of calls) {
    const value = call?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}

export function normalizePhoneForCallHistory(phone) {
  const rawValue = String(phone || "").trim();

  if (!rawValue) {
    return "";
  }

  const digitsOnly = rawValue.replace(/\D/g, "");

  if (!digitsOnly) {
    return rawValue.toLowerCase();
  }

  if (digitsOnly.length >= 10) {
    return digitsOnly.slice(-10);
  }

  return digitsOnly;
}

export function sortCallsNewestFirst(calls = []) {
  return [...calls].sort((left, right) => {
    const timestampDiff = getCallTimestamp(right) - getCallTimestamp(left);

    if (timestampDiff !== 0) {
      return timestampDiff;
    }

    const rightId = String(right?.id || right?.call_id || "");
    const leftId = String(left?.id || left?.call_id || "");

    return rightId.localeCompare(leftId);
  });
}

export function getCallHistoryLabel(index, totalCalls) {
  const callNumber = index + 1;

  if (index === 0) {
    return `Call ${callNumber} - Latest Call`;
  }

  if (index === 1) {
    return totalCalls === 2
      ? `Call ${callNumber} - Previous Call`
      : `Call ${callNumber} - Follow-up`;
  }

  return `Call ${callNumber} - Follow-up`;
}

export function getCallHistoryItems(call = {}) {
  if (Array.isArray(call.callHistory) && call.callHistory.length) {
    return call.callHistory;
  }

  return call ? [call] : [];
}

export function getCallHistoryRequestKey(call = {}) {
  return (
    call.phoneGroupKey ||
    call.__groupKey ||
    call.call_id ||
    call.id ||
    call.phone_number ||
    "call"
  );
}

export function groupCallsByPhoneNumber(calls = []) {
  const groupedCalls = new Map();

  calls.forEach((call, index) => {
    const normalizedPhone = normalizePhoneForCallHistory(call?.phone_number);
    const fallbackKey = String(call?.phone_number || call?.id || call?.call_id || `call-${index}`);
    const groupKey = normalizedPhone || fallbackKey;

    if (!groupedCalls.has(groupKey)) {
      groupedCalls.set(groupKey, []);
    }

    groupedCalls.get(groupKey).push(call);
  });

  return sortCallsNewestFirst(
    Array.from(groupedCalls.entries()).map(([groupKey, groupItems], groupIndex) => {
      const sortedCalls = sortCallsNewestFirst(groupItems).map((call, index) => ({
        ...call,
        __historyIndex: index,
        __historyKey: String(
          call?.id ||
            call?.call_id ||
            `${groupKey}-${index}-${call?.created_at || "no-date"}`,
        ),
        __historyLabel: getCallHistoryLabel(index, groupItems.length),
      }));
      const latestCall = sortedCalls[0] || {};

      return {
        ...latestCall,
        caller_name:
          latestCall.caller_name || getFirstPresentValue(sortedCalls, "caller_name"),
        phone_number:
          latestCall.phone_number || getFirstPresentValue(sortedCalls, "phone_number"),
        __groupKey: `${groupKey}-${groupIndex}`,
        phoneGroupKey: groupKey,
        phoneGroupCount: sortedCalls.length,
        groupedByPhone: sortedCalls.length > 1,
        callHistory: sortedCalls,
      };
    }),
  );
}

export async function hydrateCallHistoryGroup(call = {}, loadCallDetail) {
  const callHistory = getCallHistoryItems(call);

  const detailedCalls = await Promise.all(
    callHistory.map(async (historyCall) => {
      if (!historyCall?.call_id && !historyCall?.id) {
        return historyCall;
      }

      try {
        const detail = await loadCallDetail(historyCall.call_id || historyCall.id);

        return {
          ...historyCall,
          ...detail,
        };
      } catch (error) {
        return historyCall;
      }
    }),
  );

  const [detailedGroup] = groupCallsByPhoneNumber(detailedCalls);
  return detailedGroup || call;
}
